import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

export interface ReceiptGroupPrintData {
  id: string;
  refNumber: string;
  description: string;
  groupDate: string;
  receiptCount: number;
  totalAmount: number;
  receipts: Array<{
    id: string;
    amount: number;
    currency: string;
    paidAt: string;
    method: string;
    reference: string;
    paymentType: string;
    installment: {
      id: string;
      issuedAt: string;
      dueDate: string;
      status: string;
      total: number;
      currency: string;
      fee: {
        type: string;
        billingCycle: string;
      };
    };
  }>;
  tenant: {
    fullName: string;
    emails: string[];
    phoneNumbers: string[];
  };
  lease: {
    startDate: string;
    endDate: string;
    status: string;
    currency: string;
    refNumber: string;
    monthlyRent?: number;
    unit: {
      number: string;
      floor: number;
      rooms: number;
      bathrooms: number;
      areaM2: number;
      property: {
        code: string;
        contact?: string;
        project: {
          name: string;
          number?: string;
          street?: string;
          cityName: string;
          countryName: string;
        };
      };
    };
  };
  installments?: Array<{
    id: string;
    issuedAt: string;
    dueDate: string;
    status: string;
    total: number;
    paidAmount: number;
    remainingAmount: number;
    currency: string;
    fee: {
      type: string;
      billingCycle: string;
    };
    payments?: Array<{
      receiptId: string;
      receiptReference: string;
      receiptGroupId: string;
      receiptGroupReference: string;
      amountPaid: number;
      paidAt: string;
      paymentMethod: string;
      paymentType: string;
      currency: string;
    }> | null;
  }> | null;
  includeInstallments: boolean;
}

export async function generateReceiptGroupPdf(
  data: ReceiptGroupPrintData
): Promise<{ blobUrl: string; blob: Blob }> {
  try {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Enhanced Colors
    const primaryColor = rgb(0.16, 0.5, 0.73); // Professional blue
    const primaryDark = rgb(0.1, 0.35, 0.55);
    const successColor = rgb(0.2, 0.64, 0.32); // Rich green
    const accentColor = rgb(0.95, 0.61, 0.07); // Gold accent
    const textColor = rgb(0.13, 0.13, 0.13);
    const textSecondary = rgb(0.45, 0.45, 0.45);
    const lightGray = rgb(0.96, 0.97, 0.98);
    const borderColor = rgb(0.85, 0.87, 0.89);
    const white = rgb(1, 1, 1);

    // Helper functions
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const formatDateTime = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    let page = pdfDoc.addPage([595.28, 841.89]); // A4
    const { width, height } = page.getSize();
    let yPosition = height - 40;

    // ===== ENHANCED HEADER SECTION =====
    // Top colored band
    page.drawRectangle({
      x: 0,
      y: height - 80,
      width: width,
      height: 80,
      color: primaryColor
    });

    // Decorative accent line
    page.drawRectangle({
      x: 0,
      y: height - 85,
      width: width,
      height: 5,
      color: accentColor
    });

    // Title
    page.drawText('RECEIPT GROUP', {
      x: 50,
      y: yPosition,
      size: 26,
      font: fontBold,
      color: white
    });

    // Subtitle
    page.drawText('Official Payment Receipt Document', {
      x: 50,
      y: yPosition - 20,
      size: 9,
      font: font,
      color: rgb(0.95, 0.95, 0.95)
    });

    yPosition -= 60;

    // ===== REFERENCE & DATE CARD =====
    page.drawRectangle({
      x: 40,
      y: yPosition - 55,
      width: width - 80,
      height: 65,
      color: white,
      borderColor: borderColor,
      borderWidth: 1
    });

    // Left side accent
    page.drawRectangle({
      x: 40,
      y: yPosition - 55,
      width: 4,
      height: 65,
      color: primaryColor
    });

    // Reference Number
    page.drawText('REFERENCE NUMBER', {
      x: 60,
      y: yPosition - 10,
      size: 9,
      font: fontBold,
      color: textSecondary
    });

    page.drawText(data.refNumber, {
      x: 60,
      y: yPosition - 30,
      size: 20,
      font: fontBold,
      color: primaryColor
    });

    // Date on the right
    page.drawText('ISSUE DATE', {
      x: width - 200,
      y: yPosition - 10,
      size: 9,
      font: fontBold,
      color: textSecondary
    });

    page.drawText(formatDateTime(data.groupDate), {
      x: width - 200,
      y: yPosition - 27,
      size: 11,
      font: fontBold,
      color: textColor
    });

    yPosition -= 80;

    // ===== TENANT & PROPERTY INFORMATION SECTION =====
    // Section title with icon bar
    page.drawRectangle({
      x: 40,
      y: yPosition - 2,
      width: 4,
      height: 14,
      color: accentColor
    });

    page.drawText('TENANT & PROPERTY INFORMATION', {
      x: 50,
      y: yPosition,
      size: 12,
      font: fontBold,
      color: primaryDark
    });

    yPosition -= 20;

    // Information card
    page.drawRectangle({
      x: 40,
      y: yPosition - 105,
      width: width - 80,
      height: 115,
      color: lightGray,
      borderColor: borderColor,
      borderWidth: 1
    });

    yPosition -= 15;

    // Tenant Details (Left Column)
    page.drawText('TENANT', {
      x: 55,
      y: yPosition,
      size: 8,
      font: fontBold,
      color: textSecondary
    });

    yPosition -= 15;

    page.drawText(data.tenant.fullName || 'N/A', {
      x: 55,
      y: yPosition,
      size: 11,
      font: fontBold,
      color: textColor
    });

    yPosition -= 15;

    page.drawText(data.tenant.emails?.[0] || 'No email', {
      x: 55,
      y: yPosition,
      size: 9,
      font: font,
      color: textSecondary
    });

    yPosition -= 13;

    page.drawText(data.tenant.phoneNumbers?.[0] || 'No phone', {
      x: 55,
      y: yPosition,
      size: 9,
      font: font,
      color: textSecondary
    });

    // Reset yPosition for right column
    yPosition += 43;

    // Property Details (Right Column)
    const midX = width / 2 + 20;

    page.drawText('PROPERTY', {
      x: midX,
      y: yPosition,
      size: 8,
      font: fontBold,
      color: textSecondary
    });

    yPosition -= 15;

    page.drawText(data.lease.unit.property.code || 'N/A', {
      x: midX,
      y: yPosition,
      size: 11,
      font: fontBold,
      color: textColor
    });

    yPosition -= 15;

    page.drawText(`Unit ${data.lease.unit.number} • Floor ${data.lease.unit.floor}`, {
      x: midX,
      y: yPosition,
      size: 9,
      font: font,
      color: textSecondary
    });

    yPosition -= 13;

    const propertyAddress = data.lease.unit.property.project.street ||
                           data.lease.unit.property.project.cityName ||
                           'No address';

    page.drawText(propertyAddress.substring(0, 35), {
      x: midX,
      y: yPosition,
      size: 9,
      font: font,
      color: textSecondary
    });

    yPosition -= 20;

    // Lease Period (Full width at bottom)
    page.drawLine({
      start: { x: 50, y: yPosition },
      end: { x: width - 50, y: yPosition },
      thickness: 0.5,
      color: borderColor
    });

    yPosition -= 15;

    page.drawText('LEASE PERIOD:', {
      x: 55,
      y: yPosition,
      size: 8,
      font: fontBold,
      color: textSecondary
    });

    page.drawText(
      `${formatDate(data.lease.startDate)} — ${formatDate(data.lease.endDate)}`,
      {
        x: 135,
        y: yPosition,
        size: 9,
        font: fontBold,
        color: textColor
      }
    );

    page.drawText('STATUS:', {
      x: midX,
      y: yPosition,
      size: 8,
      font: fontBold,
      color: textSecondary
    });

    page.drawText(data.lease.status || 'N/A', {
      x: midX + 50,
      y: yPosition,
      size: 9,
      font: fontBold,
      color: successColor
    });

    yPosition -= 35;

    // ===== PAYMENT SUMMARY SECTION =====
    // Section title
    page.drawRectangle({
      x: 40,
      y: yPosition - 2,
      width: 4,
      height: 14,
      color: accentColor
    });

    page.drawText('PAYMENT SUMMARY', {
      x: 50,
      y: yPosition,
      size: 12,
      font: fontBold,
      color: primaryDark
    });

    yPosition -= 20;

    // Prominent summary card
    page.drawRectangle({
      x: 40,
      y: yPosition - 60,
      width: width - 80,
      height: 70,
      color: rgb(0.95, 0.99, 0.97),
      borderColor: successColor,
      borderWidth: 2
    });

    yPosition -= 20;

    // Total Amount with icon effect
    page.drawRectangle({
      x: 55,
      y: yPosition - 5,
      width: 3,
      height: 25,
      color: successColor
    });

    page.drawText('TOTAL AMOUNT', {
      x: 65,
      y: yPosition + 10,
      size: 9,
      font: fontBold,
      color: textSecondary
    });

    page.drawText(
      `${data.lease.currency} ${data.totalAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`,
      {
        x: 65,
        y: yPosition - 10,
        size: 22,
        font: fontBold,
        color: successColor
      }
    );

    // Receipt Count
    page.drawText('RECEIPT COUNT', {
      x: midX + 20,
      y: yPosition + 10,
      size: 9,
      font: fontBold,
      color: textSecondary
    });

    page.drawText(data.receiptCount.toString(), {
      x: midX + 20,
      y: yPosition - 10,
      size: 18,
      font: fontBold,
      color: primaryColor
    });

    yPosition -= 35;

    // Description if available
    if (data.description && data.description.trim()) {
      const description =
        data.description.length > 60
          ? data.description.substring(0, 60) + '...'
          : data.description;

      page.drawText('NOTE:', {
        x: 55,
        y: yPosition,
        size: 8,
        font: fontBold,
        color: textSecondary
      });

      page.drawText(description, {
        x: 90,
        y: yPosition,
        size: 8,
        font: font,
        color: textColor
      });
    }

    yPosition -= 45;

    // ===== RECEIPTS TABLE =====
    // Section title
    page.drawRectangle({
      x: 40,
      y: yPosition - 2,
      width: 4,
      height: 14,
      color: accentColor
    });

    page.drawText('RECEIPT DETAILS', {
      x: 50,
      y: yPosition,
      size: 12,
      font: fontBold,
      color: primaryDark
    });

    yPosition -= 20;

    // Group receipts by method, payment type, currency, and fee type
    const groupedReceipts = data.receipts.reduce((acc, receipt) => {
      const feeType = receipt.installment?.fee?.type || 'N/A';
      const key = `${receipt.method}|${receipt.paymentType}|${receipt.currency}|${feeType}`;

      if (!acc[key]) {
        acc[key] = {
          method: receipt.method,
          paymentType: receipt.paymentType,
          currency: receipt.currency,
          feeType: feeType,
          totalAmount: 0,
          receiptCount: 0
        };
      }

      acc[key].totalAmount += receipt.amount;
      acc[key].receiptCount += 1;

      return acc;
    }, {} as Record<string, {
      method: string;
      paymentType: string;
      currency: string;
      feeType: string;
      totalAmount: number;
      receiptCount: number;
    }>);

    const groupedReceiptsList = Object.values(groupedReceipts);

    // Table styling
    const tableStartY = yPosition;
    const rowHeight = 20;
    const headerHeight = 25;

    // Table header with professional styling
    page.drawRectangle({
      x: 40,
      y: yPosition - headerHeight,
      width: width - 80,
      height: headerHeight,
      color: primaryColor
    });

    // Header labels with white text
    page.drawText('#', { x: 48, y: yPosition - 16, size: 9, font: fontBold, color: white });
    page.drawText('FEE TYPE', { x: 75, y: yPosition - 16, size: 9, font: fontBold, color: white });
    page.drawText('METHOD', { x: 230, y: yPosition - 16, size: 9, font: fontBold, color: white });
    page.drawText('PAYMENT TYPE', { x: 330, y: yPosition - 16, size: 9, font: fontBold, color: white });
    page.drawText('AMOUNT', { x: 450, y: yPosition - 16, size: 9, font: fontBold, color: white });

    yPosition -= headerHeight;

    // Table Rows with enhanced styling
    groupedReceiptsList.forEach((group, index) => {
      // Check if we need a new page
      if (yPosition < 120) {
        page = pdfDoc.addPage([595.28, 841.89]);
        yPosition = height - 50;

        // Redraw table header on new page
        page.drawRectangle({
          x: 40,
          y: yPosition - headerHeight,
          width: width - 80,
          height: headerHeight,
          color: primaryColor
        });

        page.drawText('#', { x: 48, y: yPosition - 16, size: 9, font: fontBold, color: white });
        page.drawText('FEE TYPE', { x: 75, y: yPosition - 16, size: 9, font: fontBold, color: white });
        page.drawText('METHOD', { x: 230, y: yPosition - 16, size: 9, font: fontBold, color: white });
        page.drawText('PAYMENT TYPE', { x: 330, y: yPosition - 16, size: 9, font: fontBold, color: white });
        page.drawText('AMOUNT', { x: 450, y: yPosition - 16, size: 9, font: fontBold, color: white });

        yPosition -= headerHeight;
      }

      // Enhanced alternating row colors with borders
      const rowColor = index % 2 === 0 ? rgb(0.99, 0.99, 0.99) : white;

      page.drawRectangle({
        x: 40,
        y: yPosition - rowHeight,
        width: width - 80,
        height: rowHeight,
        color: rowColor,
        borderColor: rgb(0.92, 0.92, 0.92),
        borderWidth: 0.5
      });

      // Row number
      page.drawText((index + 1).toString(), {
        x: 48,
        y: yPosition - 13,
        size: 8,
        font: fontBold,
        color: textSecondary
      });

      // Fee type
      const feeTypeText = group.feeType;
      const feeType =
        feeTypeText.length > 20
          ? feeTypeText.substring(0, 20) + '...'
          : feeTypeText;

      page.drawText(feeType, {
        x: 75,
        y: yPosition - 13,
        size: 8,
        font: fontBold,
        color: textColor
      });

      // Payment method
      page.drawText(group.method || 'N/A', {
        x: 230,
        y: yPosition - 13,
        size: 8,
        font: font,
        color: textColor
      });

      // Payment type
      const paymentType = group.paymentType || 'N/A';
      const paymentTypeShort =
        paymentType.length > 12
          ? paymentType.substring(0, 12) + '...'
          : paymentType;

      page.drawText(paymentTypeShort, {
        x: 330,
        y: yPosition - 13,
        size: 8,
        font: font,
        color: textColor
      });

      // Amount with currency
      page.drawText(
        `${group.currency} ${group.totalAmount.toLocaleString('en-US', {
          minimumFractionDigits: 2
        })}`,
        {
          x: 450,
          y: yPosition - 13,
          size: 9,
          font: fontBold,
          color: successColor
        }
      );

      yPosition -= rowHeight;
    });

    yPosition -= 20;

    // ===== INSTALLMENTS TABLE (if included) =====
    if (data.includeInstallments && data.installments && data.installments.length > 0) {
      // Check if we need a new page
      if (yPosition < 220) {
        page = pdfDoc.addPage([595.28, 841.89]);
        yPosition = height - 50;
      }

      yPosition -= 25;

      // Section title
      page.drawRectangle({
        x: 40,
        y: yPosition - 2,
        width: 4,
        height: 14,
        color: accentColor
      });

      page.drawText('INSTALLMENT DETAILS', {
        x: 50,
        y: yPosition,
        size: 12,
        font: fontBold,
        color: primaryDark
      });

      yPosition -= 20;

      // Table Header with professional styling
      page.drawRectangle({
        x: 40,
        y: yPosition - headerHeight,
        width: width - 80,
        height: headerHeight,
        color: primaryDark
      });

      page.drawText('#', { x: 48, y: yPosition - 16, size: 8, font: fontBold, color: white });
      page.drawText('DUE DATE', { x: 70, y: yPosition - 16, size: 8, font: fontBold, color: white });
      page.drawText('FEE TYPE', { x: 140, y: yPosition - 16, size: 8, font: fontBold, color: white });
      page.drawText('TOTAL', { x: 240, y: yPosition - 16, size: 8, font: fontBold, color: white });
      page.drawText('PAID', { x: 310, y: yPosition - 16, size: 8, font: fontBold, color: white });
      page.drawText('REMAINING', { x: 375, y: yPosition - 16, size: 8, font: fontBold, color: white });
      page.drawText('STATUS', { x: 465, y: yPosition - 16, size: 8, font: fontBold, color: white });

      yPosition -= headerHeight;

      // Table Rows with enhanced styling and payment details
      data.installments.forEach((installment, index) => {
        // Calculate row height based on payment count
        const hasPayments = installment.payments && installment.payments.length > 0;
        const baseRowHeight = 22;
        const paymentRowHeight = hasPayments ? Math.min(installment.payments!.length * 12, 50) : 0;
        const totalRowHeight = baseRowHeight + paymentRowHeight;

        // Check if we need a new page
        if (yPosition < 120 + totalRowHeight) {
          page = pdfDoc.addPage([595.28, 841.89]);
          yPosition = height - 50;

          // Redraw table header
          page.drawRectangle({
            x: 40,
            y: yPosition - headerHeight,
            width: width - 80,
            height: headerHeight,
            color: primaryDark
          });

          page.drawText('#', { x: 48, y: yPosition - 16, size: 8, font: fontBold, color: white });
          page.drawText('DUE DATE', { x: 70, y: yPosition - 16, size: 8, font: fontBold, color: white });
          page.drawText('FEE TYPE', { x: 140, y: yPosition - 16, size: 8, font: fontBold, color: white });
          page.drawText('TOTAL', { x: 240, y: yPosition - 16, size: 8, font: fontBold, color: white });
          page.drawText('PAID', { x: 310, y: yPosition - 16, size: 8, font: fontBold, color: white });
          page.drawText('REMAINING', { x: 375, y: yPosition - 16, size: 8, font: fontBold, color: white });
          page.drawText('STATUS', { x: 465, y: yPosition - 16, size: 8, font: fontBold, color: white });

          yPosition -= headerHeight;
        }

        // Enhanced alternating row colors with borders
        const rowColor = index % 2 === 0 ? rgb(0.99, 0.99, 0.99) : white;

        page.drawRectangle({
          x: 40,
          y: yPosition - totalRowHeight,
          width: width - 80,
          height: totalRowHeight,
          color: rowColor,
          borderColor: rgb(0.92, 0.92, 0.92),
          borderWidth: 0.5
        });

        // Row number
        page.drawText((index + 1).toString(), {
          x: 48,
          y: yPosition - 13,
          size: 8,
          font: fontBold,
          color: textSecondary
        });

        // Due date
        page.drawText(formatDate(installment.dueDate), {
          x: 70,
          y: yPosition - 13,
          size: 7,
          font: font,
          color: textColor
        });

        // Fee type with billing cycle
        const feeTypeText = installment.fee?.type || 'N/A';
        const feeType =
          feeTypeText.length > 14
            ? feeTypeText.substring(0, 14) + '...'
            : feeTypeText;

        page.drawText(feeType, {
          x: 140,
          y: yPosition - 10,
          size: 7,
          font: fontBold,
          color: textColor
        });

        page.drawText(`(${installment.fee?.billingCycle || 'N/A'})`, {
          x: 140,
          y: yPosition - 18,
          size: 6,
          font: font,
          color: textSecondary
        });

        // Total Amount
        page.drawText(
          `${installment.currency} ${installment.total.toLocaleString('en-US', {
            minimumFractionDigits: 0
          })}`,
          {
            x: 240,
            y: yPosition - 13,
            size: 7,
            font: fontBold,
            color: textColor
          }
        );

        // Paid Amount
        const paidAmount = installment.paidAmount || 0;
        page.drawText(
          `${installment.currency} ${paidAmount.toLocaleString('en-US', {
            minimumFractionDigits: 0
          })}`,
          {
            x: 310,
            y: yPosition - 13,
            size: 7,
            font: fontBold,
            color: paidAmount > 0 ? successColor : textSecondary
          }
        );

        // Remaining Amount
        const remaining = installment.remainingAmount || installment.total;
        page.drawText(
          `${installment.currency} ${remaining.toLocaleString('en-US', {
            minimumFractionDigits: 0
          })}`,
          {
            x: 375,
            y: yPosition - 13,
            size: 7,
            font: fontBold,
            color: remaining > 0 ? accentColor : successColor
          }
        );

        // Status
        const statusText = installment.status || 'New';
        page.drawText(statusText, {
          x: 465,
          y: yPosition - 13,
          size: 7,
          font: fontBold,
          color: statusText === 'Paid' ? successColor : statusText === 'PartiallyPaid' ? accentColor : textSecondary
        });

        // Payment details section
        if (hasPayments) {
          yPosition -= 22;

          // Group payments by receipt group
          const paymentGroups = installment.payments!.reduce((acc, payment) => {
            const groupRef = payment.receiptGroupReference;
            if (!acc[groupRef]) {
              acc[groupRef] = {
                reference: groupRef,
                payments: [],
                totalAmount: 0,
                latestDate: payment.paidAt
              };
            }
            acc[groupRef].payments.push(payment);
            acc[groupRef].totalAmount += payment.amountPaid;
            if (new Date(payment.paidAt) > new Date(acc[groupRef].latestDate)) {
              acc[groupRef].latestDate = payment.paidAt;
            }
            return acc;
          }, {} as Record<string, { reference: string; payments: any[]; totalAmount: number; latestDate: string }>);

          // Draw payment groups
          const groups = Object.values(paymentGroups);
          let paymentY = yPosition;

          groups.forEach((group, idx) => {
            if (idx < 3) { // Limit to first 3 payment groups to save space
              // Payment group box
              page.drawRectangle({
                x: 50,
                y: paymentY - 10,
                width: width - 100,
                height: 12,
                color: rgb(0.95, 0.98, 0.96),
                borderColor: successColor,
                borderWidth: 0.5
              });

              // Group reference
              page.drawText(group.reference, {
                x: 55,
                y: paymentY - 7,
                size: 6,
                font: fontBold,
                color: primaryColor
              });

              // Payment date
              page.drawText(`Paid: ${formatDateTime(group.latestDate)}`, {
                x: 160,
                y: paymentY - 7,
                size: 6,
                font: font,
                color: textSecondary
              });

              // Total amount
              page.drawText(`Total: ${installment.currency} ${group.totalAmount.toLocaleString('en-US')}`, {
                x: 310,
                y: paymentY - 7,
                size: 6,
                font: fontBold,
                color: successColor
              });

              // Receipt count
              page.drawText(`(${group.payments.length} receipt${group.payments.length > 1 ? 's' : ''})`, {
                x: 440,
                y: paymentY - 7,
                size: 6,
                font: font,
                color: textSecondary
              });

              paymentY -= 12;
            }
          });

          if (groups.length > 3) {
            page.drawText(`... and ${groups.length - 3} more payment group(s)`, {
              x: 55,
              y: paymentY - 5,
              size: 6,
              font: font,
              color: textSecondary
            });
          }
        }

        yPosition -= totalRowHeight;
      });

      yPosition -= 15;
    }

    // ===== ENHANCED FOOTER =====
    // Add footer on all pages
    const pages = pdfDoc.getPages();
    pages.forEach((p, index) => {
      // Footer colored band
      p.drawRectangle({
        x: 0,
        y: 0,
        width: width,
        height: 65,
        color: rgb(0.97, 0.97, 0.97)
      });

      // Top accent line
      p.drawRectangle({
        x: 0,
        y: 65,
        width: width,
        height: 2,
        color: accentColor
      });

      // Footer content
      p.drawText('OFFICIAL DOCUMENT', {
        x: 50,
        y: 45,
        size: 8,
        font: fontBold,
        color: primaryColor
      });

      p.drawText('This is a system-generated receipt group document.', {
        x: 50,
        y: 32,
        size: 7,
        font: font,
        color: textSecondary
      });

      // Page number with styled box
      const pageText = `Page ${index + 1} of ${pages.length}`;
      const pageTextWidth = 60;

      p.drawRectangle({
        x: width - 110,
        y: 38,
        width: pageTextWidth,
        height: 16,
        color: white,
        borderColor: primaryColor,
        borderWidth: 1
      });

      p.drawText(pageText, {
        x: width - 100,
        y: 43,
        size: 8,
        font: fontBold,
        color: primaryColor
      });

      // Generated timestamp
      p.drawText(`Generated: ${new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`, {
        x: 50,
        y: 18,
        size: 7,
        font: font,
        color: textSecondary
      });

      // Watermark-style reference
      p.drawText(data.refNumber, {
        x: width - 150,
        y: 18,
        size: 7,
        font: font,
        color: rgb(0.7, 0.7, 0.7)
      });
    });

    // Save PDF
    const pdfBytes = await pdfDoc.save();
    const uint8 = new Uint8Array(pdfBytes);
    const blob = new Blob([uint8.buffer], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    return { blobUrl, blob };
  } catch (error) {
    console.error('Error generating receipt group PDF:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while generating the receipt group PDF');
  }
}
